import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  MenuItem,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  IconButton,
  Tooltip,
} from '@mui/material';
import { Download, AccountBalance, CloudUpload, Link as LinkIcon, Delete } from '@mui/icons-material';
import api from '../services/api';
import { Store } from '../types';

type ReconStatus = 'matched' | 'mismatch' | 'potential_match' | 'unmatched';

interface PotentialMatch {
  distributor_id: number;
  distributor_name: string;
  purchase_amount: number;
  bill_nos: string[];
}

interface PotentialBankMatch {
  bank_tx_id: number;
  date: string;
  particulars: string;
  amount: number;
}

interface DebitRow {
  bank_tx_id: number;
  date: string;
  particulars: string;
  amount: number;
  distributor_id: number | null;
  distributor_name: string | null;
  bill_count: number;
  bill_nos: string[];
  purchase_amount: number | null;
  bank_total: number | null;
  status: ReconStatus;
  potential_match: PotentialMatch | null;
}

interface UnpaidPurchaseRow {
  distributor_id: number;
  distributor_name: string;
  purchase_amount: number;
  bill_count: number;
  bill_nos: string[];
  potential_match: PotentialBankMatch | null;
}

interface ReconResponse {
  store_id: number;
  date: string;
  days: number;
  tolerance: number;
  summary: {
    total_debits: number;
    matched: number;
    mismatch: number;
    potential_match: number;
    unmatched: number;
    unpaid_purchases: number;
  };
  debits: DebitRow[];
  unpaid_purchases: UnpaidPurchaseRow[];
}

interface DistributorOption {
  id: number;
  distributor_name: string;
}

interface BankDistributorAlias {
  id: number;
  store_id: number;
  pattern: string;
  distributor_id: number;
  distributor_name: string;
  created_by: string;
  created_at: string;
}

interface Props {
  selectedStore: Store | null;
}

const inr = (n: number | null) =>
  n == null
    ? '—'
    : `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const billsLabel = (count: number, nos: string[]) =>
  count > 0 ? `${count} (${(nos || []).join(', ')})` : '—';

const statusChip = (status: ReconStatus) => {
  switch (status) {
    case 'matched':
      return <Chip size="small" color="success" label="Matched" />;
    case 'mismatch':
      return <Chip size="small" color="warning" label="Amount mismatch" />;
    case 'potential_match':
      return <Chip size="small" color="info" variant="outlined" label="Potential amount match" />;
    default:
      return <Chip size="small" color="error" variant="outlined" label="No purchase" />;
  }
};

const PurchaseReconciliation: React.FC<Props> = ({ selectedStore }) => {
  const [stores, setStores] = useState<Store[]>([]);
  const [storeId, setStoreId] = useState<number | ''>(selectedStore?.id ?? '');
  const [date, setDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [days, setDays] = useState<number>(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ReconResponse | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState<string | null>(null);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadStoreId, setUploadStoreId] = useState<number | ''>(selectedStore?.id ?? '');

  const [distributors, setDistributors] = useState<DistributorOption[]>([]);
  const [mapDialogOpen, setMapDialogOpen] = useState(false);
  const [mapDebit, setMapDebit] = useState<DebitRow | null>(null);
  const [mapPattern, setMapPattern] = useState('');
  const [mapDistributor, setMapDistributor] = useState<DistributorOption | null>(null);
  const [mapSaving, setMapSaving] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);
  const [manageDialogOpen, setManageDialogOpen] = useState(false);
  const [aliases, setAliases] = useState<BankDistributorAlias[]>([]);
  const [aliasesLoading, setAliasesLoading] = useState(false);

  useEffect(() => {
    api.get('/lookup/stores')
      .then((res) => setStores(res.data))
      .catch(() => setStores([]));
    api.get('/utilities/distributors')
      .then((res) => setDistributors(res.data.data))
      .catch(() => setDistributors([]));
  }, []);

  const handleOpenMapDialog = (debit: DebitRow) => {
    setMapDebit(debit);
    setMapPattern('');
    setMapError(null);
    setMapDistributor(
      debit.potential_match
        ? distributors.find((d) => d.id === debit.potential_match!.distributor_id) ?? null
        : null
    );
    setMapDialogOpen(true);
  };

  const handleSaveAlias = async () => {
    if (!storeId || !mapDistributor || !mapPattern.trim()) return;
    setMapSaving(true);
    setMapError(null);
    try {
      await api.post('/utilities/bank-distributor-aliases', {
        store_id: storeId,
        pattern: mapPattern.trim(),
        distributor_id: mapDistributor.id,
      });
      setMapDialogOpen(false);
      await handleLoad();
    } catch (e: any) {
      setMapError(e?.response?.data?.message || e?.response?.data?.error || 'Failed to save mapping.');
    } finally {
      setMapSaving(false);
    }
  };

  const handleOpenManageDialog = () => {
    if (!storeId) return;
    setManageDialogOpen(true);
    setAliasesLoading(true);
    api.get('/utilities/bank-distributor-aliases', { params: { store_id: storeId } })
      .then((res) => setAliases(res.data.data))
      .catch(() => setAliases([]))
      .finally(() => setAliasesLoading(false));
  };

  const handleDeleteAlias = async (id: number) => {
    try {
      await api.delete(`/utilities/bank-distributor-aliases/${id}`);
      setAliases((prev) => prev.filter((a) => a.id !== id));
      if (result && result.store_id === storeId) await handleLoad();
    } catch (e) {
      // leave the list as-is; user can retry
    }
  };

  const handleLoad = async () => {
    if (!storeId || !date) {
      setError('Please select a branch and a date.');
      return;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const res = await api.get<ReconResponse>('/utilities/purchase-reconciliation', {
        params: { store_id: storeId, date, days },
      });
      setResult(res.data);
    } catch (e) {
      setError('Failed to load reconciliation. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenUploadDialog = () => {
    setUploadStoreId(storeId || (selectedStore?.id ?? ''));
    setUploadDialogOpen(true);
  };

  const handleUpload = async () => {
    if (!uploadStoreId) {
      setError('Please select a branch before uploading.');
      return;
    }
    if (!file) {
      setError('Please choose a bank statement PDF.');
      return;
    }
    setUploadDialogOpen(false);
    setUploading(true);
    setError(null);
    setUploadMsg(null);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('store_id', String(uploadStoreId));
      const res = await api.post('/utilities/purchase-reconciliation/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const d = res.data;
      setUploadMsg(
        `Stored ${d.transactions_inserted}/${d.transactions_parsed} transactions across ${d.summary_days} day(s): ${(d.dates || []).join(', ')}`
      );
      setFile(null);
      // Auto-run reconciliation for the selected date once data is in
      await handleLoad();
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.response?.data?.error || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const downloadCsv = () => {
    if (!result) return;
    const esc = (s: string) => `"${(s || '').replace(/"/g, "'")}"`;
    const rows: string[] = ['Date,Description,Amount,Distributor,Bills,Purchase Total,Total Paid,Status'];
    result.debits.forEach((r) =>
      rows.push([
        r.date,
        esc(r.particulars),
        r.amount,
        esc(r.distributor_name || (r.potential_match ? `${r.potential_match.distributor_name} (potential)` : '')),
        esc(r.distributor_name
          ? billsLabel(r.bill_count, r.bill_nos)
          : (r.potential_match ? billsLabel(r.potential_match.bill_nos.length, r.potential_match.bill_nos) : '')),
        r.purchase_amount ?? r.potential_match?.purchase_amount ?? '',
        r.bank_total ?? '',
        r.status,
      ].join(',')));
    if (result.unpaid_purchases.length) {
      rows.push('');
      rows.push('Unpaid Purchases (no bank debit found)');
      rows.push('Distributor,Bills,Purchase Total,Potential Bank Match');
      result.unpaid_purchases.forEach((r) =>
        rows.push([
          esc(r.distributor_name),
          esc(billsLabel(r.bill_count, r.bill_nos)),
          r.purchase_amount,
          esc(r.potential_match ? `${r.potential_match.date} ${r.potential_match.amount}` : ''),
        ].join(',')));
    }
    const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `Purchase_Reconciliation_${result.date}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select a branch and date to reconcile bank debits against distributor purchases.
        Every bank debit is listed with its auto-matched distributor, the purchase bills it
        covers, and the total purchase amount, so you can confirm each payment over the
        chosen day window.
      </Typography>

      <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <TextField
            select fullWidth size="small" label="Branch" value={storeId}
            onChange={(e) => setStoreId(Number(e.target.value))}
          >
            {stores.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.store_name}</MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid item xs={12} sm={3}>
          <TextField
            fullWidth size="small" type="date" label="Date" InputLabelProps={{ shrink: true }}
            value={date} onChange={(e) => setDate(e.target.value)}
          />
        </Grid>
        <Grid item xs={6} sm={2}>
          <TextField
            fullWidth size="small" type="number" label="± Days" inputProps={{ min: 0, max: 60 }}
            value={days} onChange={(e) => setDays(Math.max(0, parseInt(e.target.value) || 0))}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Button
            variant="contained" fullWidth onClick={handleLoad}
            disabled={loading || !storeId || !date} startIcon={<AccountBalance />}
          >
            {loading ? 'Loading…' : 'Reconcile'}
          </Button>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#fafafa' }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Upload bank statement (Canara PDF):
          </Typography>
          <Button component="label" variant="outlined" size="small" startIcon={<CloudUpload />} disabled={uploading}>
            {file ? file.name : 'Choose PDF'}
            <input hidden type="file" accept="application/pdf"
              onChange={(e) => setFile(e.target.files?.[0] || null)} />
          </Button>
          <Button variant="contained" size="small" onClick={handleOpenUploadDialog}
            disabled={uploading || !file}>
            {uploading ? 'Uploading…' : 'Upload & Parse'}
          </Button>
          {uploading && <CircularProgress size={20} />}
        </Box>
        {uploadMsg && <Alert severity="success" sx={{ mt: 1 }}>{uploadMsg}</Alert>}
      </Paper>

      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Select Branch for Upload</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Which branch does <strong>{file?.name}</strong> belong to?
          </Typography>
          <TextField
            select fullWidth size="small" label="Branch" value={uploadStoreId}
            onChange={(e) => setUploadStoreId(Number(e.target.value))}
          >
            {stores.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.store_name}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpload} disabled={!uploadStoreId}>
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading && <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>}

      {result && !loading && (
        <Box>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2, alignItems: 'center' }}>
            <Chip label={`Debits: ${result.summary.total_debits}`} />
            <Chip color="success" label={`Matched: ${result.summary.matched}`} />
            <Chip color="warning" label={`Amount mismatch: ${result.summary.mismatch}`} />
            <Chip color="info" variant="outlined" label={`Potential match: ${result.summary.potential_match}`} />
            <Chip color="error" variant="outlined" label={`No purchase: ${result.summary.unmatched}`} />
            <Chip color="error" label={`Unpaid purchases: ${result.summary.unpaid_purchases}`} />
            <Box sx={{ flexGrow: 1 }} />
            <Button size="small" variant="outlined" disabled={!storeId} onClick={handleOpenManageDialog}>
              Manage mappings
            </Button>
            <Button size="small" startIcon={<Download />} onClick={downloadCsv}>CSV</Button>
          </Box>

          <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Date</strong></TableCell>
                  <TableCell><strong>Debit Description</strong></TableCell>
                  <TableCell align="right"><strong>Amount</strong></TableCell>
                  <TableCell><strong>Distributor</strong></TableCell>
                  <TableCell><strong>Purchase Bills</strong></TableCell>
                  <TableCell align="right"><strong>Purchase Total</strong></TableCell>
                  <TableCell align="center"><strong>Status</strong></TableCell>
                  <TableCell align="center"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {result.debits.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ color: 'text.secondary' }}>
                      No bank debits found for this branch and window.
                    </TableCell>
                  </TableRow>
                )}
                {result.debits.map((r, i) => {
                  // Show distributor / bills / purchase total / status once per distributor group.
                  // Rows are pre-grouped by the backend, so a "new group" starts whenever the
                  // distributor_id differs from the previous row.
                  const prev = i > 0 ? result.debits[i - 1] : null;
                  const firstOfGroup =
                    r.distributor_id == null || !prev || prev.distributor_id !== r.distributor_id;
                  return (
                    <TableRow key={r.bank_tx_id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.date}</TableCell>
                      <TableCell sx={{ fontSize: 12 }}>{r.particulars}</TableCell>
                      <TableCell align="right">{inr(r.amount)}</TableCell>
                      <TableCell>
                        {firstOfGroup
                          ? (r.distributor_name
                              ?? (r.potential_match
                                    ? <span style={{ fontStyle: 'italic', color: '#0277bd' }}>
                                        {r.potential_match.distributor_name}?
                                      </span>
                                    : <em style={{ color: '#999' }}>—</em>))
                          : <span style={{ color: '#bbb' }}>↳</span>}
                      </TableCell>
                      <TableCell sx={{ fontSize: 12 }}>
                        {firstOfGroup
                          ? (r.distributor_id != null
                              ? billsLabel(r.bill_count, r.bill_nos)
                              : (r.potential_match ? billsLabel(r.potential_match.bill_nos.length, r.potential_match.bill_nos) : ''))
                          : ''}
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ color: r.status === 'mismatch' ? 'warning.main' : 'inherit' }}
                      >
                        {firstOfGroup && r.distributor_id != null ? (
                          <>
                            {inr(r.purchase_amount)}
                            <Typography variant="caption" display="block" color="text.secondary">
                              paid {inr(r.bank_total)}
                            </Typography>
                          </>
                        ) : firstOfGroup && r.potential_match ? (
                          <Typography variant="caption" color="text.secondary">
                            {inr(r.potential_match.purchase_amount)}
                          </Typography>
                        ) : ''}
                      </TableCell>
                      <TableCell align="center">
                        {firstOfGroup ? statusChip(r.status) : ''}
                      </TableCell>
                      <TableCell align="center">
                        {firstOfGroup && (r.status === 'unmatched' || r.status === 'potential_match') && (
                          <Tooltip title="Map this bank text to a distributor">
                            <IconButton size="small" onClick={() => handleOpenMapDialog(r)}>
                              <LinkIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {result.unpaid_purchases.length > 0 && (
            <>
              <Divider sx={{ my: 1 }} />
              <Typography variant="subtitle1" sx={{ color: 'error.main', fontWeight: 600, mb: 1 }}>
                🔴 Unpaid Purchases — no bank debit found ({result.unpaid_purchases.length})
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Distributor</strong></TableCell>
                      <TableCell><strong>Purchase Bills</strong></TableCell>
                      <TableCell align="right"><strong>Purchase Total</strong></TableCell>
                      <TableCell><strong>Potential Bank Match</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {result.unpaid_purchases.map((r) => (
                      <TableRow key={r.distributor_id}>
                        <TableCell>{r.distributor_name}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>{billsLabel(r.bill_count, r.bill_nos)}</TableCell>
                        <TableCell align="right">{inr(r.purchase_amount)}</TableCell>
                        <TableCell sx={{ fontSize: 12 }}>
                          {r.potential_match
                            ? <span style={{ color: '#0277bd' }}>{r.potential_match.date} · {inr(r.potential_match.amount)}</span>
                            : <span style={{ color: '#bbb' }}>—</span>}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      )}

      <Dialog open={mapDialogOpen} onClose={() => setMapDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Map Bank Text to Distributor</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Debit: <strong>{mapDebit?.particulars}</strong> ({inr(mapDebit?.amount ?? null)})
          </Typography>
          <TextField
            fullWidth size="small" label="Text pattern to recognize" sx={{ mb: 2 }}
            value={mapPattern} onChange={(e) => setMapPattern(e.target.value)}
            helperText="A recurring fragment from the debit description above, e.g. a person's name"
          />
          <Autocomplete
            options={distributors}
            getOptionLabel={(o) => o.distributor_name}
            value={mapDistributor}
            onChange={(_, v) => setMapDistributor(v)}
            renderInput={(params) => <TextField {...params} label="Distributor" size="small" />}
          />
          {mapError && <Alert severity="error" sx={{ mt: 2 }}>{mapError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMapDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!mapPattern.trim() || !mapDistributor || mapSaving}
            onClick={handleSaveAlias}
          >
            {mapSaving ? 'Saving…' : 'Save & Re-run'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={manageDialogOpen} onClose={() => setManageDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bank ↔ Distributor Mappings</DialogTitle>
        <DialogContent>
          {aliasesLoading && <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={24} /></Box>}
          {!aliasesLoading && aliases.length === 0 && (
            <Typography variant="body2" color="text.secondary">No mappings saved for this branch yet.</Typography>
          )}
          {!aliasesLoading && aliases.length > 0 && (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>Pattern</strong></TableCell>
                  <TableCell><strong>Distributor</strong></TableCell>
                  <TableCell><strong>Saved by</strong></TableCell>
                  <TableCell align="center"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {aliases.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell sx={{ fontSize: 12 }}>{a.pattern}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{a.distributor_name}</TableCell>
                    <TableCell sx={{ fontSize: 12 }}>{a.created_by}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Delete mapping">
                        <IconButton size="small" onClick={() => handleDeleteAlias(a.id)}>
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManageDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PurchaseReconciliation;
